import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cotizacionesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSucursal } from '../context/SucursalContext';
import { formatDate } from '../utils/dateHelpers';

const CotizacionesLista = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { sucursalActual } = useSucursal();
  const [cotizaciones, setCotizaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estadisticas, setEstadisticas] = useState(null);
  const [filtros, setFiltros] = useState({
    estado: '',
    fecha_desde: '',
    fecha_hasta: ''
  });
  const [busqueda, setBusqueda] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const cargarCotizaciones = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        tienda_id: sucursalActual?.id || '',
        ...filtros
      };

      // Si no es admin, filtrar solo por sus cotizaciones
      if (user?.rol !== 'admin') {
        params.creado_por = user?.id;
      }

      const response = await cotizacionesApi.getAll(params);
      setCotizaciones(response.data || []);
      setPagination(prev => ({
        ...prev,
        ...response.pagination
      }));
    } catch (error) {
      console.error('Error al cargar cotizaciones:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, sucursalActual, filtros, user]);

  const cargarEstadisticas = useCallback(async () => {
    try {
      const params = {
        tienda_id: sucursalActual?.id || ''
      };
      if (user?.rol !== 'admin') {
        params.creado_por = user?.id;
      }
      const stats = await cotizacionesApi.getStats(params);
      setEstadisticas(stats);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  }, [sucursalActual, user]);

  useEffect(() => {
    cargarCotizaciones();
    cargarEstadisticas();
  }, [cargarCotizaciones, cargarEstadisticas]);

  const handleBuscar = async () => {
    if (busqueda.length < 2) {
      cargarCotizaciones();
      return;
    }
    setLoading(true);
    try {
      const response = await cotizacionesApi.search(busqueda);
      setCotizaciones(response.data || []);
    } catch (error) {
      console.error('Error en búsqueda:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatearFecha = formatDate;

  const formatearMoneda = (valor) => {
    return `Q.${parseFloat(valor || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;
  };

  const getEstadoClass = (estado) => {
    const clases = {
      pendiente: 'status-pendiente',
      enviada: 'status-proceso',
      aceptada: 'status-terminada',
      rechazada: 'status-entregada',
      vencida: 'status-badge'
    };
    return clases[estado] || 'status-badge';
  };

  const getEstadoLabel = (estado) => {
    const labels = {
      pendiente: 'Pendiente',
      enviada: 'Enviada',
      aceptada: 'Aceptada',
      rechazada: 'Rechazada',
      vencida: 'Vencida'
    };
    return labels[estado] || estado;
  };

  const getEstadoCount = (estado) => {
    if (!estadisticas?.por_estado) return 0;
    const found = estadisticas.por_estado.find(e => e.estado === estado);
    return found?.total || 0;
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Cotizaciones</h1>
          <p className="page-subtitle">Gestión de cotizaciones para clientes</p>
        </div>
        <Link to="/cotizaciones/nueva" className="btn btn-primary">
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          Nueva Cotización
        </Link>
      </div>

      {/* Estadísticas */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon orange">
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-info">
            <h3>{getEstadoCount('pendiente')}</h3>
            <p>Pendientes</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon blue">
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="stat-info">
            <h3>{getEstadoCount('enviada')}</h3>
            <p>Enviadas</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-info">
            <h3>{getEstadoCount('aceptada')}</h3>
            <p>Aceptadas</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon red">
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <div className="stat-info">
            <h3>{estadisticas?.tasa_conversion || 0}%</h3>
            <p>Tasa conversión</p>
          </div>
        </div>
      </div>

      {/* Filtros y búsqueda */}
      <div className="card">
        <div className="filters-bar">
          <div className="search-input">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Buscar por número, cliente o teléfono..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleBuscar()}
            />
          </div>
          <select
            name="estado"
            className="form-control"
            value={filtros.estado}
            onChange={handleFiltroChange}
            style={{ width: 'auto', minWidth: '150px' }}
          >
            <option value="">Todos los estados</option>
            <option value="pendiente">Pendiente</option>
            <option value="enviada">Enviada</option>
            <option value="aceptada">Aceptada</option>
            <option value="rechazada">Rechazada</option>
            <option value="vencida">Vencida</option>
          </select>
          <input
            type="date"
            name="fecha_desde"
            className="form-control"
            value={filtros.fecha_desde}
            onChange={handleFiltroChange}
            style={{ width: 'auto' }}
          />
          <input
            type="date"
            name="fecha_hasta"
            className="form-control"
            value={filtros.fecha_hasta}
            onChange={handleFiltroChange}
            style={{ width: 'auto' }}
          />
          <button className="btn btn-secondary" onClick={handleBuscar}>
            Buscar
          </button>
        </div>

        {/* Tabla de cotizaciones */}
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        ) : cotizaciones.length === 0 ? (
          <div className="empty-state">
            <svg width="80" height="80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3>No hay cotizaciones</h3>
            <p>Crea tu primera cotización para comenzar</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>No. Cotización</th>
                    <th>Fecha</th>
                    <th>Cliente</th>
                    <th>Teléfono</th>
                    <th>Total</th>
                    <th>Estado</th>
                    <th>Vence</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cotizaciones.map((cotizacion) => (
                    <tr key={cotizacion.id}>
                      <td>
                        <strong>{cotizacion.numero_cotizacion}</strong>
                      </td>
                      <td>{formatearFecha(cotizacion.fecha_emision)}</td>
                      <td>{cotizacion.cliente_nombre}</td>
                      <td>{cotizacion.cliente_telefono || '-'}</td>
                      <td><strong>{formatearMoneda(cotizacion.total)}</strong></td>
                      <td>
                        <span className={`status-badge ${getEstadoClass(cotizacion.estado)}`}>
                          {getEstadoLabel(cotizacion.estado)}
                        </span>
                      </td>
                      <td>{formatearFecha(cotizacion.fecha_vencimiento)}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => navigate(`/cotizaciones/${cotizacion.id}`)}
                            title="Ver cotización"
                          >
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          {cotizacion.estado === 'pendiente' && (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => navigate(`/cotizaciones/editar/${cotizacion.id}`)}
                              title="Editar"
                            >
                              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                          {cotizacion.cliente_telefono && (
                            <a
                              href={`https://wa.me/502${cotizacion.cliente_telefono.replace(/\D/g, '')}?text=${encodeURIComponent(`Hola ${cotizacion.cliente_nombre}, le enviamos su cotización ${cotizacion.numero_cotizacion} por un total de ${formatearMoneda(cotizacion.total)}`)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-success"
                              title="Enviar por WhatsApp"
                            >
                              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                              </svg>
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {pagination.pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  Anterior
                </button>
                <span style={{ padding: '6px 12px' }}>
                  Página {pagination.page} de {pagination.pages}
                </span>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                >
                  Siguiente
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CotizacionesLista;
